import { useState, useEffect } from 'react';
import { X, FolderOpen, Loader2, Trash2, Share2 } from 'lucide-react';
import { deckService, authService, DeckRecord } from '../lib/supabase';

interface MyDecksModalProps {
    isOpen: boolean;
    onClose: () => void;
    onLoadDeck: (deck: DeckRecord) => void;
}

export function MyDecksModal({ isOpen, onClose, onLoadDeck }: MyDecksModalProps) {
    const [decks, setDecks] = useState<DeckRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState<any>(null);

    useEffect(() => {
        if (isOpen) {
            loadMyDecks();
        }
    }, [isOpen]);

    const loadMyDecks = async () => {
        setLoading(true);
        const currentUser = await authService.getCurrentUser();
        setUser(currentUser);

        if (currentUser) {
            try {
                const userDecks = await deckService.getUserDecks(currentUser.id);
                setDecks(userDecks);
            } catch (err) {
                console.error('Failed to load decks:', err);
            }
        }
        setLoading(false);
    };

    const handleDelete = async (deckId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!confirm('Delete this deck?')) return;

        try {
            await deckService.deleteDeck(deckId);
            setDecks(decks.filter(d => d.id !== deckId));
        } catch (err) {
            console.error('Failed to delete deck:', err);
        }
    };

    const handleLoad = (deck: DeckRecord) => {
        onLoadDeck(deck);
        onClose();
    };

    const handleCopyLink = (deck: DeckRecord, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!deck.deck_code) return;
        const url = `${window.location.origin}/?d=${deck.deck_code}`;
        navigator.clipboard.writeText(url).then(() => {
            // Brief visual feedback
            const btn = e.currentTarget as HTMLButtonElement;
            btn.title = 'Copied!';
            setTimeout(() => { btn.title = 'Copy share link'; }, 1500);
        });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] p-4">
            <div className="bg-slate-900 border border-white/10 rounded-xl p-6 w-full max-w-md relative max-h-[70vh] flex flex-col">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-white/40 hover:text-white/60"
                >
                    <X className="size-5" />
                </button>

                <div className="flex items-center gap-3 mb-4">
                    <FolderOpen className="size-6 text-cyan-400" />
                    <h2 className="text-xl font-bold text-white">My Decks</h2>
                </div>

                <div className="flex-1 overflow-y-auto">
                    {loading ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="size-6 text-cyan-400 animate-spin" />
                        </div>
                    ) : !user ? (
                        <p className="text-white/50 text-center py-8">Sign in to view your decks</p>
                    ) : decks.length === 0 ? (
                        <p className="text-white/50 text-center py-8">No saved decks yet</p>
                    ) : (
                        <div className="space-y-2">
                            {decks.map(deck => (
                                <button
                                    key={deck.id}
                                    onClick={() => handleLoad(deck)}
                                    className="w-full flex items-center justify-between p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors group text-left"
                                >
                                    <div className="flex-1 min-w-0">
                                        <p className="text-white font-medium truncate">{deck.name}</p>
                                        <p className="text-white/40 text-xs">
                                            {deck.format} • {deck.cards?.length || 0} cards
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
                                        {deck.deck_code && (
                                            <button
                                                onClick={(e) => handleCopyLink(deck, e)}
                                                className="p-1.5 text-cyan-400/40 hover:text-cyan-400 hover:bg-cyan-500/10 rounded"
                                                title="Copy share link"
                                            >
                                                <Share2 className="size-4" />
                                            </button>
                                        )}
                                        <button
                                            onClick={(e) => handleDelete(deck.id, e)}
                                            className="p-1.5 text-red-400/40 hover:text-red-400 hover:bg-red-500/10 rounded"
                                            title="Delete deck"
                                        >
                                            <Trash2 className="size-4" />
                                        </button>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <button
                    onClick={onClose}
                    className="mt-4 w-full py-2 text-white/40 hover:text-white/60 text-sm"
                >
                    Close
                </button>
            </div>
        </div>
    );
}
